import { Link } from 'react-router-dom'
import { AlertTriangle, Gavel, Shield, Users, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { SEOHead } from '@/components/SEOHead'

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <SEOHead
        title="Terms of Service | Sycek OSINT"
        description="Sycek OSINT terms of service — acceptable use policy, platform access rules, and legal terms for our intelligence platform."
        canonical="/terms-of-service"
      />
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-purple-900/20 to-red-900/20"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f61a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f61a_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      {/* Header */}
      <div className="relative z-10 border-b border-cyan-500/30 bg-black/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(34,211,238,0.15)]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.jpeg" 
              alt="Sycek Logo" 
              className="h-10 w-10 object-contain drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,211,238,0.6)] font-mono">
                SYCEK OSINT
              </h1>
              <p className="text-xs text-green-400 font-mono drop-shadow-[0_0_5px_rgba(34,197,94,0.6)]">[SYSTEM ACTIVE]</p>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/50">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Gavel className="w-8 h-8 text-purple-400" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-cyan-400 to-green-400 bg-clip-text text-transparent">
              Terms of Service
            </h1>
          </div>
          <p className="text-gray-400 text-sm font-mono">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <Card className="bg-gray-900/50 border border-cyan-500/30 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <Users className="w-6 h-6 mr-2" />
                1. Acceptance of Terms
              </h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  By accessing or using Sycek OSINT ("Service", "Platform"), you agree to be bound by these Terms of Service ("Terms"). 
                  If you disagree with any part of these terms, you may not access the Service.
                </p>
                <p>
                  <strong className="text-yellow-400">Eligibility:</strong> You must be at least 18 years old and have the legal capacity to enter into binding agreements.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-2" />
                2. Account Registration
              </h2>
              <div className="text-gray-300 space-y-3">
                <p>To use our Service, you must:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain and update your account information to keep it accurate</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Not use temporary or disposable email addresses</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">3. Acceptable Use</h2>
              <div className="text-gray-300 space-y-3">
                <p>You agree <strong className="text-red-400">NOT</strong> to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the Service for any illegal purposes or in violation of any laws</li>
                  <li>Conduct unauthorized investigations of individuals without proper legal authority</li>
                  <li>Harvest, collect, or store personal information for malicious purposes</li>
                  <li>Impersonate any person or entity or misrepresent your affiliation</li>
                  <li>Interfere with or disrupt the Service or servers</li>
                  <li>Attempt to gain unauthorized access to any portion of the Service</li>
                  <li>Use automated systems (bots, scrapers) to access the Service without authorization</li>
                  <li>Resell or redistribute access to the Service without written permission</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                  <li>Use the Service to violate others' privacy rights or data protection laws</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">4. Service Description</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  Sycek OSINT provides open source intelligence (OSINT) investigation tools and services, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Breach intelligence and data leak analysis</li>
                  <li>Geospatial intelligence and satellite imagery analysis</li>
                  <li>Social media monitoring and analysis</li>
                  <li>Threat detection and monitoring capabilities</li>
                  <li>Network and identity correlation tools</li>
                </ul>
                <p className="mt-4">
                  <strong className="text-yellow-400">Service Availability:</strong> We strive for 99.9% uptime but do not guarantee uninterrupted access. 
                  We reserve the right to modify, suspend, or discontinue any part of the Service at any time.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">5. Subscription Tiers and Billing</h2>
              <div className="text-gray-300 space-y-3">
                <p><strong className="text-cyan-400">Subscription Plans:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Starter:</strong> Free tier with limited features and 5 queries per month</li>
                  <li><strong>Pro:</strong> $49/month with 100 queries per month</li>
                  <li><strong>Advance:</strong> $99/month with 250 queries per month</li>
                  <li><strong>Enterprise:</strong> Custom pricing with unlimited or negotiated query limits</li>
                </ul>
                <p className="mt-4">
                  <strong className="text-yellow-400">Billing Terms:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Subscriptions are billed in advance on a monthly or annual basis</li>
                  <li>Automatic renewal unless cancelled before the billing period</li>
                  <li>Refunds are provided only for unused portions of cancelled subscriptions within 30 days</li>
                  <li>Price changes will be communicated 30 days in advance</li>
                  <li>Unused queries do not roll over to the next billing period (unless specified in your plan)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">6. Intellectual Property</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  <strong className="text-cyan-400">Our Rights:</strong> The Service, including all software, graphics, logos, and content, 
                  is the property of Sycek OSINT and protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p>
                  <strong className="text-cyan-400">Your Rights:</strong> You retain ownership of data and content you provide. 
                  By using the Service, you grant us a license to use, process, and store your data solely for providing the Service.
                </p>
                <p>
                  <strong className="text-yellow-400">Restrictions:</strong> You may not copy, modify, distribute, or create derivative works 
                  based on the Service without express written permission.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-2" />
                7. Disclaimer of Warranties
              </h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  <strong className="text-red-400">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</strong>
                </p>
                <p>We disclaim all warranties, including:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Merchantability and fitness for a particular purpose</li>
                  <li>Accuracy, completeness, or reliability of intelligence results</li>
                  <li>Uninterrupted or error-free operation</li>
                  <li>Freedom from viruses or harmful components</li>
                  <li>Third-party data accuracy or availability</li>
                </ul>
                <p className="mt-4">
                  Intelligence results are based on publicly available data and should be verified independently. 
                  We do not guarantee the accuracy or completeness of any intelligence findings.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">8. Limitation of Liability</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  To the maximum extent permitted by law, Sycek OSINT shall not be liable for:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Indirect, incidental, special, or consequential damages</li>
                  <li>Loss of profits, revenue, data, or business opportunities</li>
                  <li>Damages resulting from use or inability to use the Service</li>
                  <li>Decisions made based on intelligence results provided by the Service</li>
                  <li>Unauthorized access to or alteration of your data</li>
                </ul>
                <p className="mt-4">
                  Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">9. Indemnification</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  You agree to indemnify and hold harmless Sycek OSINT, its affiliates, officers, employees, and agents from any claims, 
                  damages, losses, liabilities, and expenses (including legal fees) arising from:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your use or misuse of the Service</li>
                  <li>Violation of these Terms</li>
                  <li>Violation of any third-party rights</li>
                  <li>Your investigations or use of intelligence results</li>
                  <li>Any content or data you provide through the Service</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">10. Termination</h2>
              <div className="text-gray-300 space-y-3">
                <p><strong className="text-cyan-400">By You:</strong> You may terminate your account at any time through your account settings or by contacting support.</p>
                <p><strong className="text-cyan-400">By Us:</strong> We may suspend or terminate your account immediately if you:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Violate these Terms or our policies</li>
                  <li>Engage in fraudulent, abusive, or illegal activity</li>
                  <li>Fail to pay subscription fees</li>
                  <li>Pose a security risk to the Service or other users</li>
                </ul>
                <p className="mt-4">
                  Upon termination, your right to use the Service ceases immediately. 
                  We may delete your account and data after a reasonable retention period as specified in our Privacy Policy.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">11. Governing Law and Dispute Resolution</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  These Terms are governed by the laws of Bangalore, Karnataka, INDIA, without regard to conflict of law principles.
                </p>
                <p>
                  Any disputes arising from these Terms or the Service shall be resolved through:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Good faith negotiations between the parties</li>
                  <li>Binding arbitration in accordance with the Arbitration and Conciliation Act, 2015 (India), if negotiations fail</li>
                  <li>Courts of Bangalore, Karnataka, INDIA for matters not subject to arbitration</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">12. Changes to Terms</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  We reserve the right to modify these Terms at any time. Material changes will be communicated via:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Email notification to registered users</li>
                  <li>Prominent notice on our platform</li>
                  <li>Updated "Last updated" date</li>
                </ul>
                <p className="mt-4">
                  Continued use of the Service after changes constitutes acceptance of the modified Terms.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">13. Contact Information</h2>
              <div className="text-gray-300 space-y-3">
                <p>For questions about these Terms of Service:</p>
                <div className="bg-gray-800/50 border border-cyan-500/30 rounded-lg p-4 font-mono">
                  <p><strong className="text-cyan-400">Email:</strong> legal@sycek.io</p>
                  <p><strong className="text-cyan-400">Support:</strong> support@sycek.io</p>
                  <p><strong className="text-cyan-400">Mailing Address:</strong> Sycek OSINT, Bangalore KA, INDIA</p>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

